<template>
  <div class="skeleton-loader">
    <span class="sr-only">Loading...</span>
    <div
      v-for="item in numberOfItems"
      :key="item"
      class="skeleton-loader-item"
      :class="itemClasses"
      :style="itemStyle"
    />
  </div>
</template>

<script lang="ts">

/**
 * Placeholder for content that is still loading, drawn as one or more waving bars.
 */

import { defineComponent, computed } from 'vue';

export default defineComponent({
  name: 'SkeletonLoader',
  props: {
    /** Height of a single bar, either a css length or a number of pixels. */
    height: {
      type: [String, Number],
      required: true,
    },
    /** Width of a single bar, either a css length or a number of pixels. */
    width: {
      type: [String, Number],
      default: '100%',
    },
    /** How many bars to draw, for content that is a list of similar items. */
    numberOfItems: {
      type: Number,
      default: 1,
    },
    /** Classes for every single bar, usually the spacing between them. */
    itemClasses: {
      type: String,
      default: '',
    },
  },
  setup(props) {

    const toCssLength = (value: string | number) => typeof value === 'number' ? `${value}px` : value;

    const itemStyle = computed(() => ({
      height: toCssLength(props.height),
      width: toCssLength(props.width),
    }));

    return {
      itemStyle,
    };

  },
});

</script>

<!--
  Same wave as vue3-skeleton-loader, with its default base color, highlight color and timing,
  but written as plain css instead of the v-bind() css variables the library uses, since Vue
  does not apply those during SSR and the skeleton would stay invisible until hydration.
-->
<style scoped>
.skeleton-loader-item {
  position: relative;
  overflow: hidden;
  border-radius: 0.25rem;
  background-color: rgb(0 0 0 / 12%);
}

.skeleton-loader-item::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: linear-gradient(90deg, transparent, rgb(255 255 255 / 40%), transparent);
  animation: skeleton-wave 1.5s linear infinite;
}

@keyframes skeleton-wave {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}
</style>
